import styled from '@emotion/styled';
import { useLingui } from '@lingui/react/macro';
import { CircleUserRound, Shield } from 'lucide-react';
import {
  getKeyDescription,
  getPickableKeys,
  getRegistryEntry,
  pickIdentityFields,
} from 'modules/manager/domain/placeholder/placeholder-key-registry';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import type { PlaceholderKind } from 'modules/manager/domain/placeholder/types';
import type { TemplateType } from 'modules/manager/domain/template-type';
import { editorAccent } from 'modules/manager/ui/components/editor/editor-accent';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { theme } from 'styles/theme/theme';
import UDButton from 'ud-ui/components/button';
import UDEmptyState from 'ud-ui/components/empty-state';
import UDModal from 'ud-ui/components/modal';
import UDSearchInput from 'ud-ui/components/search';
import { KEY_GROUP_LABELS, groupKeys } from './key-groups';
import KeyIdentityFields, { PickedIdentity } from './key-identity-fields';
import { TemplateScheme, isKeyOnScheme } from './template-scheme';

interface PlaceholderKeyPickerModalProps {
  kind: PlaceholderKind;
  /** Unknown (still loading) — every key of the kind is offered, no scheme and no slot numbers. */
  templateType?: TemplateType;
  onPick: (key: PlaceholderKey, identity: PickedIdentity) => void;
  onClose: () => void;
}

// Image keys have no text sample — a glyph tells a logo from a photo at a glance.
const IMAGE_ICONS: Partial<Record<PlaceholderKey, JSX.Element>> = {
  [PlaceholderKey.TEAM_LOGO]: <Shield strokeWidth={1.5} />,
  [PlaceholderKey.TOURNAMENT_LOGO]: <Shield strokeWidth={1.5} />,
  [PlaceholderKey.PLAYER_PHOTO]: <CircleUserRound strokeWidth={1.5} />,
};

const defaultIdentity = (key: PlaceholderKey | undefined, templateType?: TemplateType): PickedIdentity =>
  key ? Object.fromEntries(pickIdentityFields(key, templateType).map((field) => [field, 1])) : {};

/**
 * Mount only while shown: the parent renders it conditionally, so a reopen starts
 * clean. A click only selects — the details are the point of the window; a double
 * click, Enter or «Добавить на холст» adds.
 */
const PlaceholderKeyPickerModal: React.FC<PlaceholderKeyPickerModalProps> = ({ kind, templateType, onPick, onClose }) => {
  const { t } = useLingui();
  const [query, setQuery] = useState('');
  const rowRefs = useRef(new Map<PlaceholderKey, HTMLButtonElement>());
  // A double click must not drop two placeholders: the first pick wins, the
  // modal unmounts right after.
  const picked = useRef(false);

  const sections = useMemo(() => {
    const needle = query.trim();
    // From the start of a word: a bare substring finds «очки» inside «карточки».
    const pattern = needle ? new RegExp(`(^|[^\\p{L}\\p{N}])${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'iu') : null;
    const keys = getPickableKeys(kind, templateType).filter((key) => {
      if (!pattern) return true;
      const entry = getRegistryEntry(key);
      const description = getKeyDescription(key, templateType);
      return entry && description ? pattern.test(`${t(entry.label)} ${t(description)}`) : false;
    });
    return groupKeys(keys);
  }, [kind, templateType, query, t]);
  const visible = useMemo(() => sections.flatMap((section) => section.keys), [sections]);

  const [selected, setSelected] = useState<PlaceholderKey | undefined>(visible[0]);
  const [identity, setIdentity] = useState<PickedIdentity>(() => defaultIdentity(visible[0], templateType));
  const current = selected && visible.includes(selected) ? selected : visible[0];
  const entry = current ? getRegistryEntry(current) : undefined;
  const fields = current ? pickIdentityFields(current, templateType) : [];

  const select = (key: PlaceholderKey) => {
    if (key === current) return;
    setSelected(key);
    setIdentity(defaultIdentity(key, templateType));
    rowRefs.current.get(key)?.scrollIntoView?.({ block: 'nearest' });
  };

  // A filter that hides the selected key moves the selection to the first match;
  // its slot numbers must follow, or the old key's fields would leak in.
  useEffect(() => {
    if (current && current !== selected) {
      setSelected(current);
      setIdentity(defaultIdentity(current, templateType));
    }
  }, [current, selected, templateType]);

  // A double click names its row: the selection may not have re-rendered yet.
  const add = (key: PlaceholderKey | undefined = current) => {
    if (!key || picked.current) return;
    picked.current = true;
    const chosen = key === current ? identity : {};
    const picks = Object.fromEntries(pickIdentityFields(key, templateType).map((field) => [field, chosen[field] ?? 1]));
    onPick(key, picks);
    onClose();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!current) return;
      event.preventDefault();
      const at = visible.indexOf(current) + (event.key === 'ArrowDown' ? 1 : -1);
      const next = visible[Math.min(Math.max(at, 0), visible.length - 1)];
      if (next) select(next);
      return;
    }
    if (event.key !== 'Enter') return;
    // Enter on «Отмена», the close cross or a home/away switch keeps its own meaning.
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON' && !target.hasAttribute('data-key')) return;
    event.preventDefault();
    add();
  };

  const onScheme = templateType && current ? isKeyOnScheme(templateType, current) : false;

  return (
    <UDModal show onClose={onClose} width={920} height={620}>
      <UDModal.Header title={kind === 'placeholder-image' ? t`Выберите изображение` : t`Выберите данные`} />
      <Layout onKeyDown={onKeyDown}>
        <Split>
          <ListColumn>
            <UDSearchInput
              // The modal mounts its content through a portal, after a parent effect
              // could reach it — the input focuses itself instead.
              autoFocus
              value={query}
              placeholder={t`Поиск`}
              iconPosition="icon-left"
              onChange={(event) => setQuery(event.target.value)}
            />
            <Rows role="listbox" aria-label={t`Данные`}>
              {sections.length === 0 ? (
                <UDEmptyState
                  className="px-2 pt-10 text-center"
                  title={t`Ничего не нашлось`}
                  subtitle={t`Попробуйте «команда», «дата» или «логотип».`}
                />
              ) : (
                sections.map((section) => (
                  <React.Fragment key={section.group}>
                    <GroupTitle>{t(KEY_GROUP_LABELS[section.group])}</GroupTitle>
                    {section.keys.map((key) => {
                      const keyEntry = getRegistryEntry(key);
                      if (!keyEntry) return null;
                      const icon = IMAGE_ICONS[key];
                      return (
                        <Row
                          key={key}
                          ref={(node) => {
                            if (node) rowRefs.current.set(key, node);
                            else rowRefs.current.delete(key);
                          }}
                          type="button"
                          role="option"
                          data-key={key}
                          aria-selected={key === current}
                          onClick={() => select(key)}
                          onDoubleClick={() => add(key)}
                        >
                          <RowName>{t(keyEntry.label)}</RowName>
                          {keyEntry.sample ? (
                            <RowSample>{t(keyEntry.sample)}</RowSample>
                          ) : (
                            icon && <RowIcon data-testid="placeholder-key-icon">{icon}</RowIcon>
                          )}
                        </Row>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
            </Rows>
          </ListColumn>
          <Detail>
            {current && entry && (
              <>
                <DetailTitle>{t(entry.label)}</DetailTitle>
                <Description>{t(getKeyDescription(current, templateType) ?? entry.description)}</Description>
                {templateType && (
                  <KeyIdentityFields templateType={templateType} fields={fields} value={identity} onChange={setIdentity} />
                )}
                {templateType &&
                  (onScheme ? (
                    <TemplateScheme templateType={templateType} highlightKey={current} />
                  ) : (
                    <Muted>{t`На схеме не показан`}</Muted>
                  ))}
                {entry.examples && entry.examples.length > 0 && (
                  <Examples>
                    <Muted>{t`Примеры`}</Muted>
                    {entry.examples.map((example) => (
                      <Example key={example.id ?? example.message}>{t(example)}</Example>
                    ))}
                  </Examples>
                )}
              </>
            )}
          </Detail>
        </Split>
        <Footer>
          <UDButton variant="secondary" onClick={onClose}>
            {t`Отмена`}
          </UDButton>
          <UDButton variant="primary" onClick={() => add()} disabled={!current}>
            {t`Добавить на холст`}
          </UDButton>
        </Footer>
      </Layout>
    </UDModal>
  );
};

export default React.memo(PlaceholderKeyPickerModal);

const Layout = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
`;

const Split = styled.div`
  display: grid;
  flex: 1;
  grid-template-columns: 270px minmax(0, 1fr);
  min-height: 0;
  border-top: 1px solid ${theme.colors.LineSubtle};
  border-bottom: 1px solid ${theme.colors.LineSubtle};
`;

const ListColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 0;
  padding: 12px 8px 12px 0;
  border-right: 1px solid ${theme.colors.LineSubtle};
`;

const Rows = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-height: 0;
  overflow: auto;
`;

const GroupTitle = styled.div`
  padding: 10px 8px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.colors.InkMuted};
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 7px 8px;
  border: 0;
  border-radius: ${theme.radii.control};
  background: transparent;
  color: ${theme.colors.InkPrimary};
  text-align: left;
  cursor: pointer;

  &:hover {
    background: ${theme.colors.SurfaceSunken};
  }

  &[aria-selected='true'] {
    background: ${editorAccent.wash};
    box-shadow: inset 2px 0 0 ${editorAccent.solid};
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px ${editorAccent.ring};
  }
`;

const RowName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 13px;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const RowSample = styled.span`
  max-width: 90px;
  overflow: hidden;
  font-size: 12px;
  color: ${theme.colors.InkMuted};
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const RowIcon = styled.span`
  display: flex;
  color: ${theme.colors.InkMuted};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const Detail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  min-height: 0;
  padding: 16px 4px 16px 20px;
  overflow: auto;
`;

const DetailTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${theme.colors.InkPrimary};
`;

const Description = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${theme.colors.InkSecondary};
`;

const Muted = styled.span`
  font-size: 13px;
  color: ${theme.colors.InkMuted};
`;

const Examples = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
`;

const Example = styled.span`
  padding: 3px 8px;
  border-radius: ${theme.radii.control};
  background: ${theme.colors.SurfaceSunken};
  font-size: 12px;
  color: ${theme.colors.InkPrimary};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
`;
